import { supabase } from '@/lib/supabase';

export interface OfficeSnapshot {
  snapshot_id: string;
  office_id: number;
  created_at: string;
  purpose: 'manual' | 'pre_restore';
  version: number;
  tape_count: number;
  byte_length: number;
  sha256: string;
  size_bytes: number;
}

export interface FullSnapshot {
  snapshot_id: string;
  created_at: string;
  purpose: 'manual' | 'pre_restore';
  schema_version: number;
  version: number;
  row_counts: Record<string, number>;
  byte_length: number;
  sha256: string;
  size_bytes: number;
}

export class BackupOperationError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'BackupOperationError';
    this.status = status;
    this.code = code;
  }
}

const apiBase = ((import.meta.env.VITE_PUBLIC_SUPABASE_URL as string) || window.location.origin).replace(/\/$/, '');

async function parseError(error: unknown): Promise<BackupOperationError> {
  const response = (error as { context?: Response })?.context;
  let body: { code?: string; message?: string } = {};
  if (response) {
    try { body = await response.clone().json(); } catch { /* SDK response may not contain JSON. */ }
  }
  const status = response?.status ?? 0;
  const message = body.message || (error instanceof Error ? error.message : '备份操作失败');
  return new BackupOperationError(status, body.code || 'BACKUP_OPERATION_FAILED', message);
}

export async function runBackupOperation<T>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke('backup-operations', { body: { action, ...payload } });
  if (error) throw await parseError(error);
  return data as T;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function filenameFrom(response: Response, fallback: string): string {
  const value = response.headers.get('content-disposition') || '';
  const match = value.match(/filename="([^"]+)"/i);
  return match?.[1] || fallback;
}

export async function downloadBackup(
  action: 'office_download' | 'full_download',
  payload: Record<string, unknown>,
  fallback: string,
): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new BackupOperationError(401, 'UNAUTHORIZED', '登录状态已失效，请重新登录');
  const response = await fetch(`${apiBase}/functions/v1/backup-operations`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...payload }),
  });
  if (!response.ok) {
    let body: { code?: string; message?: string } = {};
    try { body = await response.clone().json(); } catch { /* Keep the HTTP status. */ }
    throw new BackupOperationError(response.status, body.code || 'BACKUP_OPERATION_FAILED', body.message || '下载备份失败');
  }
  triggerDownload(await response.blob(), filenameFrom(response, fallback));
}
